import { cn } from '@/lib/cn';
import { type JSX, Show, splitProps } from 'solid-js';

export interface InputProps extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  hint?: string;
  error?: string;
  type?:
    | 'text'
    | 'email'
    | 'password'
    | 'number'
    | 'search'
    | 'tel'
    | 'url'
    | 'date'
    | 'time'
    | 'datetime-local';
}

export const Input = (props: InputProps): JSX.Element => {
  const [local, rest] = splitProps(props, ['label', 'hint', 'error', 'class', 'id']);
  const id = local.id ?? `input-${Math.random().toString(36).slice(2, 10)}`;
  return (
    <label class="flex flex-col gap-1 text-sm" for={id}>
      <Show when={local.label}>
        <span
          class={cn(
            'font-medium text-neutral-700 dark:text-neutral-300',
            rest.required && "after:ml-0.5 after:text-red-500 after:content-['*']",
          )}
        >
          {local.label}
        </span>
      </Show>
      <input
        id={id}
        class={cn(
          'h-10 rounded-lg border bg-white px-3 text-sm text-neutral-900 outline-none transition',
          'border-neutral-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-500',
          'dark:bg-neutral-900 dark:text-neutral-100 dark:border-neutral-700 dark:focus:border-brand-500',
          local.error && 'border-red-500 focus:border-red-500 focus:ring-red-500',
          local.class as string | undefined,
        )}
        {...rest}
      />
      <Show when={local.error}>
        <span class="text-xs text-red-600">{local.error}</span>
      </Show>
      <Show when={!local.error && local.hint}>
        <span class="text-xs text-neutral-500">{local.hint}</span>
      </Show>
    </label>
  );
};
